import type { ReactElement } from 'react'
import { SecondaryButton } from './SecondaryButton'
import { defaultRouteCardCopy, type RouteCardCopySet } from './copy'
import styles from './RouteEmpty.module.css'

/**
 * Props for {@link RouteEmpty}.
 */
export interface RouteEmptyProps {
  /** Tap retry handler. If omitted, no Retry button is rendered. */
  readonly onRetry?: () => void
  /** Localised copy. @defaultValue {@link defaultRouteCardCopy} */
  readonly copy?: RouteCardCopySet
}

/**
 * Empty state for the route-built panel (Figma spec §2.8). Shown when the
 * routing algorithm returned successfully but produced zero variants.
 *
 * Two rows: a centered Message block (title `semiboldPx14` / subtitle
 * `regularPx12`) and a `Route details row` with the shared
 * {@link SecondaryButton} labelled "Retry" (h 64, r 32). The button
 * emits `retryTapped` via `onRetry`.
 */
export function RouteEmpty({
  onRetry,
  copy = defaultRouteCardCopy,
}: RouteEmptyProps): ReactElement {
  return (
    <div className={styles.root} data-testid="route-empty">
      <div className={styles.message}>
        <p className={styles.title}>{copy.emptyTitle}</p>
        <p className={styles.subtitle}>{copy.emptySubtitle}</p>
      </div>
      {onRetry !== undefined && (
        <div className={styles.detailsRow}>
          <SecondaryButton label={copy.retry} onClick={onRetry} testId="route-empty-retry" />
        </div>
      )}
    </div>
  )
}
